import type * as React from 'react';
import { notFound } from '@tanstack/react-router';
import { moduleAreaForPath } from '@hetivo/hetivo-lib-types';
import { DefaultNotFoundPage } from '@hetivo/hetivo-lib-web-utils';

import { useTenantModules } from '&/hooks/use-tenant-modules';
import type { TenantModulesResult } from '&/hooks/use-tenant-modules';

/**
 * Throw the standard not-found once entitlement is known and the tenant does not own the module
 * that `path` belongs to.
 *
 * The owning module is looked up in the registry (`moduleAreaForPath`), never named by the caller, so
 * the registry stays the only place that says which module a screen belongs to. A path no module
 * declares is core and is never gated; `__module-surface-paths.spec.ts` keeps registry paths in step
 * with the real routes.
 *
 * Use it in a route's `beforeLoad` (with `context.entitlement`). A module the tenant has not bought
 * must look absent, not locked: not-found, never a redirect or an upsell page (SWOOP-276). While
 * entitlement is still loading it does nothing, so an entitled tenant is never denied early.
 *
 * On its own this is not enough. On a hard refresh `beforeLoad` runs before entitlement has loaded,
 * lets the navigation through, and is never re-run, so the page must also be wrapped in
 * {@link ModuleGate}, which applies this same rule once the answer arrives.
 */
export function requireModule(entitlement: TenantModulesResult, path: string): void {
  if (isModuleDenied(entitlement, path)) {
    throw notFound();
  }
}

/** True once entitlement is known and the tenant does not own the module `path` belongs to. */
function isModuleDenied(entitlement: TenantModulesResult, path: string): boolean {
  const area = moduleAreaForPath(path);
  return area !== undefined && entitlement.isReady && !entitlement.hasModule(area);
}

export interface ModuleGateProps {
  /** The route path of the gated screen, as declared on its registry surface. */
  path: string;
  children: React.ReactNode;
  /** Shown while entitlement is loading, in place of `children`. */
  fallback: React.ReactNode;
}

/**
 * Render `children` only for a tenant that owns the module `path` belongs to (SWOOP-277).
 *
 * - Resolved, not owned → the standard not-found page, rendered in place. Not `throw notFound()`: thrown
 *   during render it is caught by the route's error boundary, so the viewer got "Something went wrong!".
 * - Still loading → `fallback`. `children` is not mounted, so it cannot fire requests the server
 *   refuses for a tenant without the module.
 * - Path owned by no module → `children`: core screens have nothing to wait for.
 * - Lookup failed → `children`. Entitlement is unknown rather than absent, and the server still
 *   refuses anything the tenant does not own, so waiting forever would only hide the page.
 *
 * It re-renders when entitlement resolves, which is what closes the hard-refresh gap a route's
 * `beforeLoad` leaves open.
 */
export function ModuleGate({ path, children, fallback }: ModuleGateProps): React.ReactNode {
  const entitlement = useTenantModules();

  if (isModuleDenied(entitlement, path)) {
    return <DefaultNotFoundPage />;
  }

  if (moduleAreaForPath(path) !== undefined && !entitlement.isReady && !entitlement.isError) {
    return fallback;
  }

  return children;
}
